/*
 * DESIGN: Clean, professional — teal accent on dark navy base.
 * Insurance Solutions: Brokers & Consultants for the towing industry.
 * Not agents — we prepare your business to get the best policy.
 */
import { useEffect } from "react";
import Reveal from "../components/Reveal";
import Img from "../components/Img";
import {
  ShieldCheck, FileCheck, Users, TrendingUp, ClipboardList, Truck,
  ArrowRight, CheckCircle2, BarChart3, FileText, Wrench, Star,
  ChevronRight, Phone, Mail, MapPin
} from "lucide-react";
import { Link } from "wouter";

const HERO_IMG = "/images/insurance-hero-dtP6VpRsKW78EWHLgBzNmk.webp";
const PREP_IMG = "/images/insurance-preparation-MqQg9zmi6aVxuTwAYzunaj.webp";
const PARTNER_IMG = "/images/insurance-partnership-AXw9y32rw4CwBByQWtBxM7.webp";

/* ── Data ── */
const preparationAreas = [
  {
    icon: BarChart3,
    title: "Annual Record & History",
    description: "We compile and present your complete operational history — claims records, loss ratios, and annual performance data that agencies need to see.",
  },
  {
    icon: Users,
    title: "Driver Performance Reporting",
    description: "Detailed driver safety scores, training records, MVR reports, and performance metrics that demonstrate your commitment to safety.",
  },
  {
    icon: ClipboardList,
    title: "Policies & Procedures",
    description: "We help you document and present your safety policies, hiring procedures, drug testing protocols, and operational standards.",
  },
  {
    icon: FileCheck,
    title: "DOT Records & Compliance",
    description: "Clean DOT records, inspection histories, CSA scores, and regulatory compliance documentation — organized and ready for review.",
  },
  {
    icon: Wrench,
    title: "Fleet Maintenance Records",
    description: "Comprehensive maintenance logs, preventive maintenance schedules, and vehicle condition reports that prove your fleet is well-managed.",
  },
  {
    icon: TrendingUp,
    title: "US Tow Fleet Integration",
    description: "Our US Tow Fleet platform continuously tracks and documents your fleet's performance throughout the year — so you're always application-ready.",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Become a Member",
    description: "Join our network of towing businesses. As a member, you gain access to our full suite of preparation tools and the buying power of our collective pool.",
  },
  {
    step: "02",
    title: "Prepare Year-Round",
    description: "Using US Tow Fleet and our consulting guidance, we help you document, track, and improve your business metrics throughout the entire year — not just at renewal time.",
  },
  {
    step: "03",
    title: "We Package Your Application",
    description: "Our team compiles your records into a professional, comprehensive application package that presents your business in the strongest possible light.",
  },
  {
    step: "04",
    title: "Access Top Agencies & Carriers",
    description: "We present your application to the best agencies and carriers in the industry. Our collective buying power and professional presentation gets you quotes others can't.",
  },
];

const memberBenefits = [
  "Collective buying power — strength in numbers",
  "Access to top-tier agencies and carriers nationwide",
  "Year-round business preparation and consulting",
  "US Tow Fleet fleet monitoring platform",
  "Professional application packaging",
  "DOT compliance guidance and support",
  "Driver safety program development",
  "Claims management consulting",
  "Dedicated insurance consultant",
  "Annual policy review and optimization",
];

export default function InsuranceSolutions() {
  useEffect(() => {
    document.title = "Towing Insurance Brokers & Consultants | Blue Collar AI";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement("meta"); name.startsWith("og:") ? el.setAttribute("property", name) : el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Insurance brokers and consultants for the towing industry. We prepare your business year-round to secure the best commercial policy from top carriers.");
    setMeta("og:title", "Insurance Solutions — Towing Industry Insurance Brokers & Consultants");
    setMeta("og:description", "Not agents. Not seasonal. We prepare your towing business year-round to get the best insurance policy from top agencies nationwide.");
    setMeta("og:url", "https://www.bluecollarai.online/insurance-solutions");
    return () => { document.title = "Blue Collar AI — AI Consulting for Towing, Auto Shops & Blue Collar Businesses"; };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b-[3px] border-teal-500/20 bg-navy-deep/95 backdrop-blur-md">
        <div className="container flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 border-[3px] border-teal-400 rounded-sm flex items-center justify-center bg-teal-400/10">
              <ShieldCheck className="w-4 h-4 text-teal-400" />
            </div>
            <span className="font-heading font-bold text-lg text-white">
              INSURANCE <span className="text-teal-400">SOLUTIONS</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#approach" className="text-steel-light text-sm hover:text-white transition-colors">Our Approach</a>
            <a href="#preparation" className="text-steel-light text-sm hover:text-white transition-colors">What We Prepare</a>
            <a href="#process" className="text-steel-light text-sm hover:text-white transition-colors">How It Works</a>
            <a href="#membership" className="text-steel-light text-sm hover:text-white transition-colors">Membership</a>
            <a href="#contact-insurance" className="bg-teal-500 hover:bg-teal-400 text-white font-heading font-semibold text-sm px-5 py-2.5 transition-colors">
              Get Started
            </a>
          </div>
        </div>
      </nav>

      <main>
      {/* ── Hero ── */}
      <section className="relative min-h-[85vh] flex items-center pt-16">
        <div className="absolute inset-0">
          <Img src={HERO_IMG} width={1920} height={1072} sizes="100vw" priority alt="Tow truck fleet" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/90 to-navy-deep/50" />
        </div>
        <div className="container relative z-10 py-20">
          <div className="max-w-2xl">
            <Reveal from="up" immediate className="inline-flex items-center gap-2 px-4 py-1.5 border-[2px] border-teal-400/40 bg-teal-400/10 mb-6">
              <ShieldCheck className="w-4 h-4 text-teal-400" />
              <span className="font-mono text-xs text-teal-400 tracking-[0.2em] uppercase">A Blue Collar AI Company</span>
            </Reveal>

            <Reveal as="h1" from="up" immediate delay={0.1} className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              We Don't Sell Insurance.
              <br />
              <span className="text-teal-400">We Get You the Best Policy.</span>
            </Reveal>

            <Reveal as="p" from="up" immediate delay={0.2} className="text-steel-light text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              Insurance brokers and consultants for the towing industry. We prepare your business year-round so you walk into renewal season with the strongest application possible — and access to the top agencies in the country.
            </Reveal>

            <Reveal from="up" immediate delay={0.3} className="flex flex-wrap gap-4">
              <a href="#membership" className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-heading font-semibold px-8 py-4 transition-colors text-base">
                Become a Member
                <ArrowRight className="w-5 h-5" />
              </a>
              <a href="#approach" className="inline-flex items-center gap-2 border-[3px] border-white/20 hover:border-white/40 text-white font-heading font-semibold px-8 py-4 transition-colors text-base">
                Learn Our Approach
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Distinction Banner ── */}
      <section className="bg-teal-500/10 border-y-[3px] border-teal-500/20 py-12">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <Reveal from="up">
              <p className="font-heading font-bold text-2xl text-white mb-2">Not Agents</p>
              <p className="text-steel-light text-sm">We don't sell policies. We're brokers and consultants who work for <em>you</em>.</p>
            </Reveal>
            <Reveal from="up">
              <p className="font-heading font-bold text-2xl text-white mb-2">Not Seasonal</p>
              <p className="text-steel-light text-sm">We prepare your business year-round, not just at renewal time.</p>
            </Reveal>
            <Reveal from="up">
              <p className="font-heading font-bold text-2xl text-white mb-2">Collective Power</p>
              <p className="text-steel-light text-sm">Our member pool gives you the buying power of a large fleet operation.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Our Approach ── */}
      <section id="approach" className="py-24 md:py-32">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Reveal as="span" from="up" className="font-mono text-xs text-teal-400 tracking-[0.3em] uppercase block mb-4">
                // OUR APPROACH
              </Reveal>
              <Reveal as="h2" from="up" delay={0.1} className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Your Best Foot Forward,
                <br />
                <span className="text-teal-400">Every Renewal Season</span>
              </Reveal>
              <Reveal as="p" from="up" delay={0.2} className="text-steel-light text-base leading-relaxed mb-6">
                We know what insurance agencies and carriers want to see. We've spent years in the towing industry understanding exactly what it takes to get the best policy at the best rate. Our job isn't to sell you insurance — it's to make your business look as strong as possible when it's time to apply.
              </Reveal>
              <Reveal as="p" from="up" delay={0.3} className="text-steel-light text-base leading-relaxed mb-8">
                By presenting your annual records, driver performance data, safety policies, DOT compliance, and fleet maintenance history in a professional, comprehensive package, we help you stand out from the crowd. We work with and partner with the top agencies in the country — and we bring your application to them ready to impress.
              </Reveal>
              <Reveal from="up" delay={0.4} className="flex flex-col gap-3">
                {["Year-round preparation, not last-minute scrambling", "Professional application packaging", "Access to the best agencies nationwide"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-400 shrink-0" />
                    <span className="text-white text-sm font-medium">{item}</span>
                  </div>
                ))}
              </Reveal>
            </div>

            <Reveal from="right" duration={0.7} className="relative">
              <div className="border-[3px] border-teal-400/30 overflow-hidden">
                <Img src={PREP_IMG} width={1920} height={1072} sizes="(min-width: 1024px) 50vw, 100vw" alt="Business preparation" className="w-full h-auto" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-teal-500 px-6 py-3">
                <span className="font-mono text-sm text-white font-bold">365 Days of Preparation</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── What We Prepare ── */}
      <section id="preparation" className="py-24 md:py-32 bg-navy-deep relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
        <div className="container">
          <div className="mb-16">
            <Reveal as="span" from="up" className="font-mono text-xs text-teal-400 tracking-[0.3em] uppercase block mb-4">
              // WHAT WE PREPARE
            </Reveal>
            <Reveal as="h2" from="up" delay={0.1} className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Building Your <span className="text-teal-400">Strongest Application</span>
            </Reveal>
            <Reveal as="p" from="up" delay={0.2} className="text-steel-light text-lg max-w-2xl">
              We help you document, organize, and present every aspect of your business that agencies evaluate when writing a policy.
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {preparationAreas.map((area, i) => (
              <Reveal
                key={i}
                from="up"
                delay={i * 0.1}
                className="p-8 border-[3px] border-teal-400/20 hover:border-teal-400/50 bg-card transition-all duration-300 group"
              >
                <div className="w-12 h-12 border-[2px] border-teal-400/30 bg-teal-400/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <area.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-3">{area.title}</h3>
                <p className="text-steel-light text-sm leading-relaxed">{area.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="process" className="py-24 md:py-32">
        <div className="container">
          <div className="mb-16">
            <Reveal as="span" from="up" className="font-mono text-xs text-teal-400 tracking-[0.3em] uppercase block mb-4">
              // HOW IT WORKS
            </Reveal>
            <Reveal as="h2" from="up" delay={0.1} className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              From Member to <span className="text-teal-400">Best Quote</span>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <Reveal
                key={i}
                from="up"
                duration={0.5}
                delay={i * 0.12}
                className="relative p-8 border-[3px] border-border hover:border-teal-400/40 bg-card transition-all duration-300"
              >
                <span className="font-mono text-4xl font-bold text-teal-400/20 block mb-4">{step.step}</span>
                <h3 className="font-heading font-bold text-lg text-white mb-3">{step.title}</h3>
                <p className="text-steel-light text-sm leading-relaxed">{step.description}</p>
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-teal-400/40" />
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partnership Image + Membership ── */}
      <section id="membership" className="py-24 md:py-32 bg-navy-deep relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal from="left" duration={0.7} className="relative">
              <div className="border-[3px] border-teal-400/30 overflow-hidden">
                <Img src={PARTNER_IMG} width={1920} height={1072} sizes="(min-width: 1024px) 50vw, 100vw" alt="Partnership" className="w-full h-auto" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-teal-500 px-6 py-3">
                <span className="font-mono text-sm text-white font-bold">Top Agency Partners</span>
              </div>
            </Reveal>

            <div>
              <Reveal as="span" from="up" className="font-mono text-xs text-teal-400 tracking-[0.3em] uppercase block mb-4">
                // MEMBERSHIP BENEFITS
              </Reveal>
              <Reveal as="h2" from="up" delay={0.1} className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Strength in <span className="text-teal-400">Numbers</span>
              </Reveal>
              <Reveal as="p" from="up" delay={0.2} className="text-steel-light text-base leading-relaxed mb-8">
                As a member business, you benefit from being part of a large pool of towing companies that we present to agencies and carriers. This collective buying power means better rates, better coverage, and access to carriers that individual businesses often can't reach on their own.
              </Reveal>
              <Reveal from="up" delay={0.3} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {memberBenefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                    <span className="text-white text-sm">{benefit}</span>
                  </div>
                ))}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-500 relative">
        <div className="container text-center">
          <div>
            <Reveal as="h2" from="up" className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Stop Overpaying for Insurance
            </Reveal>
            <Reveal as="p" from="up" delay={0.1} className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
              Let us prepare your business for the best policy in the industry. Become a member today.
            </Reveal>
            <Reveal
              as="a"
              from="up"
              delay={0.2}
              href="#contact-insurance"
              className="inline-flex items-center gap-2 bg-white text-teal-600 font-heading font-bold px-10 py-4 hover:bg-teal-50 transition-colors text-base"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact-insurance" className="py-24 md:py-32">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <Reveal as="span" from="up" className="font-mono text-xs text-teal-400 tracking-[0.3em] uppercase block mb-4">
                // GET STARTED
              </Reveal>
              <Reveal as="h2" from="up" delay={0.1} className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Ready to Put Your
                <br />
                <span className="text-teal-400">Best Foot Forward?</span>
              </Reveal>
              <Reveal as="p" from="up" delay={0.2} className="text-steel-light text-base leading-relaxed mb-8">
                Contact us for a free consultation. We'll review your current situation and show you exactly how we can help you get a better policy at a better rate.
              </Reveal>
              <Reveal from="up" delay={0.3} className="flex flex-col gap-5">
                <a href="tel:614-633-7935" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 border-[2px] border-teal-400/30 bg-teal-400/10 flex items-center justify-center group-hover:border-teal-400/60 transition-colors">
                    <Phone className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs text-steel tracking-wider uppercase">Phone</p>
                    <p className="text-white font-medium">614-633-7935</p>
                  </div>
                </a>
                <a href="mailto:chris@bluecollarai.online" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 border-[2px] border-teal-400/30 bg-teal-400/10 flex items-center justify-center group-hover:border-teal-400/60 transition-colors">
                    <Mail className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs text-steel tracking-wider uppercase">Email</p>
                    <p className="text-white font-medium">chris@bluecollarai.online</p>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border-[2px] border-teal-400/30 bg-teal-400/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs text-steel tracking-wider uppercase">Location</p>
                    <p className="text-white font-medium">Columbus, OH — Serving Nationwide</p>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal
              from="up"
              duration={0.6}
              className="border-[3px] border-teal-400/20 bg-card p-8 md:p-10"
            >
              <h3 className="font-heading font-bold text-xl text-white mb-6">Request a Free Consultation</h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const btn = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
                  if (btn) { btn.textContent = "Message Sent!"; btn.disabled = true; }
                }}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-xs text-steel tracking-wider uppercase block mb-2">Name</label>
                    <input type="text" required className="w-full bg-navy-deep border-[2px] border-border focus:border-teal-400 px-4 py-3 text-white text-sm outline-none transition-colors" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-steel tracking-wider uppercase block mb-2">Company</label>
                    <input type="text" required className="w-full bg-navy-deep border-[2px] border-border focus:border-teal-400 px-4 py-3 text-white text-sm outline-none transition-colors" placeholder="Company name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-xs text-steel tracking-wider uppercase block mb-2">Email</label>
                    <input type="email" required className="w-full bg-navy-deep border-[2px] border-border focus:border-teal-400 px-4 py-3 text-white text-sm outline-none transition-colors" placeholder="you@company.com" />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-steel tracking-wider uppercase block mb-2">Phone</label>
                    <input type="tel" className="w-full bg-navy-deep border-[2px] border-border focus:border-teal-400 px-4 py-3 text-white text-sm outline-none transition-colors" placeholder="(555) 000-0000" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-xs text-steel tracking-wider uppercase block mb-2">Fleet Size</label>
                  <input type="text" className="w-full bg-navy-deep border-[2px] border-border focus:border-teal-400 px-4 py-3 text-white text-sm outline-none transition-colors" placeholder="Number of trucks" />
                </div>
                <div>
                  <label className="font-mono text-xs text-steel tracking-wider uppercase block mb-2">Tell Us About Your Situation</label>
                  <textarea rows={4} className="w-full bg-navy-deep border-[2px] border-border focus:border-teal-400 px-4 py-3 text-white text-sm outline-none transition-colors resize-none" placeholder="Current insurance challenges, renewal date, etc." />
                </div>
                <button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-white font-heading font-bold py-4 transition-colors text-base">
                  Request Consultation
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t-[3px] border-teal-400/20 bg-navy-deep py-8">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-teal-400" />
            <span className="font-heading font-bold text-sm text-white">
              Insurance Solutions — <span className="text-teal-400">A Blue Collar AI Company</span>
            </span>
          </div>
          <p className="font-mono text-xs text-steel">
            &copy; {new Date().getFullYear()} Blue Collar AI. All rights reserved.
          </p>
          <Link href="/" className="text-steel-light text-sm hover:text-teal-400 transition-colors flex items-center gap-1">
            <ArrowRight className="w-3 h-3 rotate-180" />
            Back to BlueCollarAI.online
          </Link>
        </div>
      </footer>
    </div>
  );
}
